import React, { useState } from 'react';
import { X, Eye, EyeOff, Lock, Shield, AlertCircle, Terminal, Fingerprint } from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { Button } from '@/components/common/ui/Button';

interface VerificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onVerify: (password: string) => Promise<void>;
    isLoading: boolean;
    error?: string;
    level?: 'first_factor' | 'second_factor' | 'multi_factor';
}

export const VerificationModal: React.FC<VerificationModalProps> = ({
    isOpen,
    onClose,
    onVerify,
    isLoading,
    error
}) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password.trim()) return;

        try {
            await onVerify(password);
            setPassword('');
        } catch (err) {
            // Error handling managed by parent component hooks
        }
    };

    const handleClose = () => {
        setPassword('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[150] p-4 font-mono">
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl shadow-[0_0_60px_rgba(0,0,0,0.9)] max-w-md w-full mx-auto overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                {/* --- Header: Handshake Protocol --- */}
                <div className="flex items-center justify-between py-5 px-6 border-b border-[#1A1D24] bg-[#12151C]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#00FFFF]/10 border border-[#00FFFF]/20 flex items-center justify-center">
                            <Fingerprint className="h-5 w-5 text-[#00FFFF]" />
                        </div>
                        <div>
                            <h2 className="text-[12px] font-bold text-white uppercase tracking-[0.2em]">
                                Handshake_Protocol
                            </h2>
                            <p className="text-[9px] text-slate-500 uppercase tracking-widest mt-0.5">
                                Verification Required to Proceed
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-slate-500 hover:text-white hover:bg-[#1A1D24] rounded-lg transition-all"
                        disabled={isLoading}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* --- Content: Identity Challenge --- */}
                <form onSubmit={handleSubmit} className="p-8 space-y-6">

                    {/* Security Checkpoint Notice */}
                    <div className="p-4 bg-[#00FFFF]/5 border border-[#00FFFF]/20 rounded-xl flex gap-3">
                        <Shield className="h-4 w-4 text-[#00FFFF] flex-shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-white uppercase tracking-widest">Security_Checkpoint</p>
                            <p className="text-[10px] text-slate-500 uppercase leading-relaxed">
                                This challenge protects your account integrity from unauthorized session modification.
                            </p>
                        </div>
                    </div>

                    {/* Error Feedback */}
                    {error && (
                        <div className="p-3 bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-lg flex items-center gap-3 animate-in shake-in">
                            <AlertCircle className="h-4 w-4 text-[#FF3333]" />
                            <span className="text-[10px] text-[#FF3333] font-bold uppercase tracking-tight">
                                {error}
                            </span>
                        </div>
                    )}

                    {/* Password Entry */}
                    <div className="space-y-2">
                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em] ml-1">
                            Current_Password_Hash
                        </label>
                        <Input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter credentials..."
                            className="bg-black border-[#1A1D24] text-[#00FFFF] h-12 text-[13px] focus:border-[#00FFFF] transition-all"
                            leftIcon={<Lock className="h-4 w-4 text-slate-700" />}
                            rightIcon={
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="text-slate-600 hover:text-white transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            }
                            disabled={isLoading}
                            autoFocus
                        />
                    </div>

                    {/* Action Matrix */}
                    <div className="flex flex-col gap-3 pt-2">
                        <Button
                            type="submit"
                            loading={isLoading}
                            disabled={!password.trim() || isLoading}
                            className="w-full bg-[#00FFFF] text-black font-black h-12 text-[11px] tracking-[0.2em] shadow-[0_0_20px_rgba(0,255,255,0.2)] hover:scale-[1.02] active:scale-95 transition-all"
                        >
                            EXECUTE_VERIFY
                        </Button>
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="w-full h-10 text-[10px] font-bold text-slate-600 hover:text-white uppercase tracking-widest transition-colors"
                        >
                            Abort_Command
                        </button>
                    </div>
                </form>

                {/* Footer Trace */}
                <div className="bg-[#050505] px-8 py-3 border-t border-[#1A1D24] flex items-center justify-between opacity-50">
                    <div className="flex items-center gap-2 text-[8px] text-slate-700 uppercase tracking-widest">
                        <Terminal className="h-3 w-3" /> Trace_ID: AUTH_{Math.random().toString(36).substring(7).toUpperCase()}
                    </div>
                    <div className="text-[8px] text-slate-700 uppercase tracking-widest">
                        Enc_AES_256
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerificationModal;